/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useEffect, useState } from 'react'
import PartnerSubnav from 'views/partners/components/subnav'
import { StandaloneLink } from '@hashicorp/mds-react/components'
import CertificationBadges from 'components/_page-components/partners/certification-badges'
import { useRouter } from 'lib/i18n/routing'
import { trackGoal as trackFathomGoal } from 'fathom-client'
import Image from 'next/image'
import {
	IntegrationsTabs,
	type IIntegrationsTabs,
} from 'components/_page-components/partners/integrations-tabs'
import SectionHeadline from 'components/materia/section-headline'
import s from './style.module.css'

type Product = 'all' | 'terraform' | 'vault' | 'consul' | 'nomad' | 'boundary'
const products: Product[] = [
	'all',
	'terraform',
	'vault',
	'consul',
	'nomad',
	'boundary',
]

function isValidProduct(product: any): product is Product {
	return products.includes(product)
}

export default function IntegrationPage({ company }) {
	const [selectedOption, setSelectedOption] = useState<Product>('all')

	const router = useRouter()

	useEffect(() => {
		const hash = window.location.hash.slice(1)
		if (!isValidProduct(hash)) return

		setSelectedOption(hash)
	}, [])

	useEffect(() => {
		router.replace(`/partners/tech/${company.slug}/#${selectedOption}`)
	}, [selectedOption, company.slug])

	const groupedIntegrations: IIntegrationsTabs['groupedIntegrations'] = {
		all: company.integrations,
	}

	company.integrations.forEach((integration) => {
		const product = integration.integrationProduct.products[0].product

		groupedIntegrations[product] = groupedIntegrations[product]
			? groupedIntegrations[product].concat(integration)
			: [integration]
	})

	const productOptions = Object.keys(groupedIntegrations).sort((a, b) =>
		a.localeCompare(b),
	)

	const panels = productOptions.map((product) => {
		return {
			name: product,
		}
	})

	const cloudVerifiedBadges = company.cloudVerified.map(
		(badge) => badge.product,
	)
	const enterpriseVerifiedBadges = company.enterpriseVerified.map(
		(badge) => badge.product,
	)

	return (
		<>
			<PartnerSubnav />
			<section className={s.content}>
				<div className={s.hero}>
					<div className="g-grid-container">
						<div className={s.heroContainer}>
							<div className={s.heroContent}>
								{company.tier ? (
									<p className={s.companyTier}>
										Partner Tier: <strong>{company.tier}</strong>
									</p>
								) : null}
								<h1>{company.name}</h1>
								<p className={s.companyDescription}>{company.description}</p>

								{cloudVerifiedBadges.length > 0 ? (
									<CertificationBadges
										heading="Cloud Verified"
										products={cloudVerifiedBadges}
									/>
								) : null}

								{enterpriseVerifiedBadges.length > 0 ? (
									<CertificationBadges
										heading="Enterprise Verified"
										products={enterpriseVerifiedBadges}
									/>
								) : null}
							</div>
							<div className={s.heroSecondary}>
								<LogoContainer
									logo={{
										...company.logo,
										alt: company.logo.alt ?? `${company.name} logo`,
									}}
									className={s.desktopLogo}
									partnerWebsiteUrl={company.partnerWebsite}
								/>
								{company.partnerWebsite && (
									<div className={s.partnerWebLink}>
										<StandaloneLink
											text="Visit partner website"
											href={company.partnerWebsite}
											onClick={() => trackExternalPartnerWebsiteClick()}
											icon="arrow-right"
											iconPosition="trailing"
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<Image
						src={require('components/_page-components/partners/images/partner-hero-graphic.svg')}
						className={s.heroGraphic}
						alt=""
						width={210}
						height={362}
					/>
				</div>
				<div className={s.heroSecondary}>
					<LogoContainer
						logo={{
							...company.logo,
							alt: company.logo.alt ?? `${company.name} logo`,
						}}
						className={s.mobileLogo}
						partnerWebsiteUrl={company.partnerWebsite}
					/>
				</div>

				{company.integrations.length > 0 && (
					<>
						<SectionHeadline
							__typename="SectionHeadlineRecord"
							layout={{
								paddingBottom: 'none',
							}}
							heading={'Integrations'}
							headingElement="h2"
							size="small"
							alignment="left"
							ctas={[]}
						/>
						<IntegrationsTabs
							groupedIntegrations={groupedIntegrations}
							panels={panels}
						/>
					</>
				)}
			</section>
		</>
	)
}

function LogoContainer({ logo, className, partnerWebsiteUrl }) {
	if (!logo) return null
	if (!partnerWebsiteUrl) {
		return (
			<div className={`${s.logoContainer} ${className}`}>
				<Image {...logo} alt={logo.alt ?? ''} />
			</div>
		)
	} else {
		return (
			<a
				className={`${s.logoContainer} ${className}`}
				href={partnerWebsiteUrl}
				onClick={() => trackExternalPartnerWebsiteClick()}
				rel="noreferrer"
				target="_blank"
			>
				<Image {...logo} alt={logo.alt ?? ''} />
			</a>
		)
	}
}

function trackExternalPartnerWebsiteClick() {
	// ID here is from hashicorp.com Fathom site - 'Visited external partner website' event
	trackFathomGoal('EUKEDSKC', 0)
}
